import { Notification, Payment, PaymentLookup, QualtricsResponseHandler, Section, Select } from "../survey";
import { noDuplicateSectionNamesRule } from "./no_duplicate_section_names_rule";
import { noDuplicateSelectValuesRule } from "./no_duplicate_select_values_rule";
import { noEmptySelectValuesRule } from "./no_empty_select_values_rule";
import { noDuplicateQualtricsSurveyIdsRule } from "./no_duplicate_qualtrics_survey_ids_rule";
import { paymentRequiredFieldsRule } from "./payment_required_fields_rule";
import { noEmptySectionTitlesRule } from "./no_empty_section_titles_rule";
import { noMalformedTargetFieldsInNotifsRule } from "./no_malformed_target_fields_in_notifs_rule";

type Rule<T extends { kind: string }> = {
  name: string;
  /** The description of the rule and tips for fixing it. Can be verbose. */
  description: string;
  severity: 'error' | 'warning';
  kinds: T['kind'][];
  fn: (
    component: Readonly<T>, 
    /** A rule-specific object that can be used to store state for the rule across multiple invocations. */
    state: Record<string, any>
  ) => { 
    passed: boolean,
    /** A short message for a failure,
     * including identifiers that help differentiate the failure from similar ones. */
    message?: string
  };
}

export const DISTRO_RULES = [
  {
    name: 'no_duplicate_section_names',
    description: 'Duplicate English section titles are not allowed, because it will unfortunately cause issues on the applicant page. Fix by making sure all English section titles are unique.',
    severity: 'warning',
    kinds: ['Section'],
    fn: noDuplicateSectionNamesRule
  } satisfies Rule<Section>,
  {
    name: 'no_empty_section_titles',
    description: 'Section titles should not be empty or contain only whitespace. Fix by providing a meaningful title for each section.',
    severity: 'error',
    kinds: ['Section'],
    fn: noEmptySectionTitlesRule
  } satisfies Rule<Section>,
  {
    name: 'no_duplicate_select_values',
    description: 'Duplicate Select values are most likely a mistake.',
    severity: 'warning',
    kinds: ['Select'],
    fn: noDuplicateSelectValuesRule
  } satisfies Rule<Select>,
  {
    name: 'no_empty_select_values',
    description: 'Empty or whitespace-only Select values are most likely a mistake.',
    severity: 'warning',
    kinds: ['Select'],
    fn: noEmptySelectValuesRule
  } satisfies Rule<Select>,
  {
    name: 'payment_required_fields',
    description: 'Payment components must have name, type, targetField, and amount or be a lookup kind.',
    severity: 'error',
    kinds: ['Payment'],
    fn: paymentRequiredFieldsRule
  } satisfies Rule<Payment | PaymentLookup>,
  {
    name: 'no_duplicate_qualtrics_survey_ids',
    description: 'Duplicate Qualtrics surveys ids makes it ambiguous which should handle a response',
    severity: 'error',
    kinds: ['Qualtrics Response Handler'],
    fn: noDuplicateQualtricsSurveyIdsRule
  } satisfies Rule<QualtricsResponseHandler>,
  {
    name: 'no_malformed_target_fields_in_notifs',
    description: 'Hidden HTML formatting can cause target field replacements to fail',
    severity: 'error',
    kinds: ['Notification', 'InlineNotification'],
    fn: noMalformedTargetFieldsInNotifsRule
  } satisfies Rule<Notification>,
]


export const DISTRO_RULES_BY_KIND = DISTRO_RULES.reduce((acc, rule) => {
  rule.kinds.forEach(kind => {
    acc[kind] ??= [];
    acc[kind].push(rule);
  });
  return acc;
}, {} as Record<string, Rule<any>[]>);
